import React, { memo } from 'react'
import classNames from 'classnames'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { AppointmentMobile } from 'apps/fleet-planner/pages/marketplace/AppointmentMobile'
import {
  formatTotalMiles,
  formatEquipment,
  formatWeight,
} from 'apps/fleet-planner/utils/marketplaceHelpers'
import { Bullet } from 'components/atoms'
import { formatPerMile, formatCurrency } from 'utils/formatCurrency'
import { IRecommendedShipmentFragment } from '../ShipmentCard/graphql/RecommendedShipmentFragment'

const useShipmentCardDetailsStyles = makeStyles(theme => ({
  dot: {
    color: brandColors.activeBlue,
  },
  textColor: {
    color: brandColors.coolGray6,
  },
  rate: {
    color: brandColors.skyBlue6,
  },
  milesEquipment: {
    padding: `${theme.spacing(1)} 0px 0px 0px`,
  },
  noPadding: {
    padding: theme.spacing(0),
  },
  cardHeader: {
    marginBottom: theme.spacing(2.5),
  },
  rateWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    marginBottom: theme.spacing(0.5),
  },
  rpm: {
    marginLeft: theme.spacing(1),
  },
  details: {
    color: brandColors.coolGray6,
  },
  stopsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    position: 'relative',
    '&.line:after': {
      content: '""',
      background: brandColors.coolGray4,
      position: 'absolute',
      height: '45%',
      width: '2px',
      top: '50%',
      left: '3px',
      transform: 'translateY(-50%)',
    },
  },
}))

export interface IShipmentCardDetailsProps {
  recommendation: IRecommendedShipmentFragment
  variant?: 'book' | 'historical'
  isNewLayoutEnabled?: boolean
}

const ShipmentCardDetails = memo<IShipmentCardDetailsProps>(
  ({ isNewLayoutEnabled = false, recommendation, variant = 'historical' }) => {
    const {
      distance = 0,
      rate = '',
      equipmentName = '',
      // Dry Van Default
      equipmentParentId = '3',
      weight,
      pickupTimes = [],
      deliveryTimes = [],
      pickupCity = '',
      pickupStateCode = '',
      deliveryCity = '',
      deliveryStateCode = '',
      pickupTimezone = 'America/New_York',
      deliveryTimezone = 'America/New_York',
      unviewed,
    } = recommendation || {}
    const [pickupStartAt, pickupEndAt] = pickupTimes
    const [deliveryStartAt, deliveryEndAt] = deliveryTimes
    const { isMobile } = useBreakpointContext()
    const classes = useShipmentCardDetailsStyles()
    const formattedMiles = distance.toString()
    const milesEquipment = classNames(classes.textColor, classes.milesEquipment)

    const stopsContainerClasses = classNames(classes.stopsContainer, { line: isNewLayoutEnabled })

    return (
      <div>
        {isNewLayoutEnabled ? (
          <div className={classes.cardHeader}>
            <div className={classes.rateWrapper}>
              <Typography variant='h3' color='primary'>
                {formatCurrency(rate)}
              </Typography>
              <div className={classes.rpm}>
                <Typography variant='body2' color='textSecondary' component='div'>
                  ${formatPerMile(rate, distance, true)}/mi
                </Typography>
              </div>
            </div>
            <div className={classes.details}>
              <Typography variant='body2' component='span'>
                {formatTotalMiles(formattedMiles)}
              </Typography>
              <Bullet />
              <Typography variant='body2' component='span'>
                {formatEquipment(equipmentName, Number(equipmentParentId))}
              </Typography>
              <Bullet />
              <Typography variant='body2' component='span'>
                {formatWeight(weight)}
              </Typography>
            </div>
          </div>
        ) : (
          <Box display='flex' pt={isMobile ? 1 : 0} marginBottom={1}>
            <Box>
              <Box display='flex' alignContent='center'>
                {unviewed && variant === 'historical' && (
                  <Box pr={1}>
                    <Typography className={classes.dot}>&#9679;</Typography>
                  </Box>
                )}
                <Typography className={classes.rate} variant='h3'>
                  {formatCurrency(rate)}
                </Typography>
              </Box>
              <Typography variant='body2' className={classes.textColor}>
                ${formatPerMile(rate, distance, true)}/mi
              </Typography>
            </Box>
            <Box display='flex' flexDirection='column' marginLeft='auto' className={milesEquipment}>
              <Typography align='right' variant='body2' className={classes.noPadding}>
                {formatTotalMiles(formattedMiles)}
              </Typography>
              <Box display='flex'>
                <Typography variant='body2' className={classes.noPadding}>
                  {formatEquipment(equipmentName, Number(equipmentParentId))}
                </Typography>
                <Box mx={0.5}>
                  <Bullet />
                </Box>
                <Typography variant='body2' className={classes.noPadding}>
                  {formatWeight(weight)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <div className={stopsContainerClasses}>
          {/* Pickup */}
          <AppointmentMobile
            startAt={pickupStartAt}
            endAt={pickupEndAt}
            city={pickupCity}
            stateCode={pickupStateCode}
            timezone={pickupTimezone}
            mapMarkerType='pickup'
            textColor='textTertiary'
          />
          {/* Dropoff */}
          <AppointmentMobile
            startAt={deliveryStartAt}
            endAt={deliveryEndAt}
            city={deliveryCity}
            stateCode={deliveryStateCode}
            timezone={deliveryTimezone}
            mapMarkerType='delivery'
            textColor='textTertiary'
          />
        </div>
      </div>
    )
  }
)

export default ShipmentCardDetails
